import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment-timezone';

import { Descriptions, Collapse, Table, Spin } from 'antd';
import {
  InfoCircleTwoTone,
  ToolFilled,
  ExclamationCircleTwoTone,
  ClockCircleFilled,
  ScheduleFilled,
  CheckSquareFilled,
} from '@ant-design/icons';

import { ReactorType } from '../../../utils/types';
import {
  getNextUnavailabilities,
  getFinishedUnavailabilities,
} from '../../../api';

function unavailabilityIcon(unavailability) {
  if (unavailability.type === 'PLANNED_MAINTENANCE') {
    return unavailability.availablePower_MW > 0 ? (
      <InfoCircleTwoTone />
    ) : (
      <ToolFilled style={{ color: '#404040' }} />
    );
  }
  return <ExclamationCircleTwoTone twoToneColor="red" />;
}

const COLUMNS = [
  {
    title: '',
    width: 40,
    render(_, unavailability) {
      return unavailabilityIcon(unavailability);
    },
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 120,
    render(type) {
      if (type === 'PLANNED') {
        return <span style={{ color: '#1890ff' }}>Planifiée</span>;
      }
      if (type === 'FORCED' || type === 'UNPLANNED') {
        return <span style={{ color: '#ff4d4f' }}>Fortuite</span>;
      }
      return type;
    },
  },
  {
    title: 'Début',
    dataIndex: 'startDate',
    key: 'startDate',
    width: 100,
    render(startDate) {
      return moment(startDate).format('DD/MM/YYYY');
    },
  },
  {
    title: 'Fin',
    dataIndex: 'endDate',
    key: 'endDate',
    width: 100,
    render(endDate) {
      return endDate ? moment(endDate).format('DD/MM/YYYY') : 'Non définie';
    },
  },
  {
    title: 'Puissance',
    key: 'power',
    width: 120,
    render(_, unavailability) {
      return (
        <div>
          <div style={{ fontSize: '12px', color: '#52c41a' }}>
            ✓ {unavailability.availablePower_MW || 0} MW
          </div>
          <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
            ✗ {unavailability.unavailablePower_MW || 0} MW
          </div>
        </div>
      );
    },
  },
  {
    title: 'Raison',
    dataIndex: 'reason',
    key: 'reason',
    ellipsis: true,
    render(reason) {
      const reasonMap = {
        PLANNED_MAINTENANCE: 'Maintenance planifiée',
        FORESEEN_MAINTENANCE: 'Maintenance prévue',
        FORCED_OUTAGE: 'Arrêt fortuit',
        FAILURE: 'Panne',
        REFUELING: 'Rechargement combustible',
        PERIODIC_SAFETY_INSPECTION: 'Visite décennale',
        COMPLEMENTARY_INFORMATION: 'Information complémentaire',
        OTHER: 'Autre',
      };
      return reasonMap[reason] || reason;
    },
  },
];

function expandedRowRender(unavailability, withType = false) {
  return (
    <Descriptions size="small" column={2} bordered>
      {withType && (
        <Descriptions.Item label="Type" span={2}>
          {unavailability.type === 'PLANNED'
            ? 'Maintenance planifiée'
            : unavailability.type === 'FORCED' ||
              unavailability.type === 'UNPLANNED'
            ? 'Arrêt fortuit'
            : unavailability.type}
        </Descriptions.Item>
      )}
      <Descriptions.Item label="Statut">
        <span
          style={{
            color:
              unavailability.status === 'ACTIVE'
                ? '#52c41a'
                : unavailability.status === 'CANCELLED'
                ? '#ff4d4f'
                : '#1890ff',
          }}
        >
          {unavailability.status === 'ACTIVE'
            ? 'Actif'
            : unavailability.status === 'CANCELLED'
            ? 'Annulé'
            : unavailability.status}
        </span>
      </Descriptions.Item>
      <Descriptions.Item label="Dernière mise à jour">
        {moment(unavailability.updatedAt).format('DD/MM/YYYY HH:mm')}
      </Descriptions.Item>
      <Descriptions.Item label="Puissance installée">
        {unavailability.installedCapacity_MW
          ? `${unavailability.installedCapacity_MW} MW`
          : 'N/A'}
      </Descriptions.Item>
      <Descriptions.Item label="Réacteur">
        {unavailability.reactorName || 'N/A'}
      </Descriptions.Item>
      {unavailability.remarks && (
        <Descriptions.Item label="Remarques" span={2}>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
            {unavailability.remarks}
          </div>
        </Descriptions.Item>
      )}
      <Descriptions.Item label="Identifiant" span={2}>
        <code style={{ fontSize: '11px' }}>{unavailability.id}</code>
      </Descriptions.Item>
    </Descriptions>
  );
}

function getInitPanel(currentUnavailability, nextUnavailabilities) {
  if (currentUnavailability) {
    return 'current';
  }

  return nextUnavailabilities && nextUnavailabilities.length > 0
    ? 'next'
    : 'done';
}

const INIT_STATE = {
  loaded: false,
  data: {},
  page: 1,
};
const PAGE_SIZE = 10;

function UnavailabilitiesTab(props) {
  const { reactor, currentDate } = props;

  const [stateDate, setDate] = useState(currentDate);
  const [nextUnavState, setNextUnav] = useState(INIT_STATE);
  const [finishedUnavState, setFinishedUnav] = useState(INIT_STATE);

  useEffect(() => {
    const nextPage = nextUnavState.page;
    const finishedPage = finishedUnavState.page;

    if (currentDate !== stateDate) {
      setDate(currentDate);
      setNextUnav(INIT_STATE);
      setFinishedUnav(INIT_STATE);
    }

    if (!nextUnavState.data[nextPage]) {
      // setNextUnav({ ...nextUnavState, loading: true });
      getNextUnavailabilities({
        date: currentDate,
        eicCode: reactor.eicCode,
        skip: (nextUnavState.page - 1) * PAGE_SIZE,
        limit: 10,
      }).then(res =>
        setNextUnav({
          ...nextUnavState,
          loaded: true,
          total: res.total,
          data: { ...nextUnavState.data, [nextPage]: res.data },
        }),
      );
    }

    if (!finishedUnavState.data[finishedPage]) {
      // setFinishedUnav({ ...finishedUnavState, loading: true });
      getFinishedUnavailabilities({
        date: currentDate,
        eicCode: reactor.eicCode,
        skip: (finishedUnavState.page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      }).then(res =>
        setFinishedUnav({
          ...finishedUnavState,
          loaded: true,
          total: res.total,
          data: { ...finishedUnavState.data, [finishedPage]: res.data },
        }),
      );
    }
  }, [
    nextUnavState,
    finishedUnavState,
    nextUnavState.page,
    finishedUnavState.page,
    reactor.eicCode,
    stateDate,
    currentDate,
  ]);

  const mCurrentDate = moment(currentDate);
  const currentUnavailability = reactor.unavailabilities.find(
    u => mCurrentDate.isAfter(u.startDate) && mCurrentDate.isBefore(u.endDate),
  );
  const initPanel = getInitPanel(
    currentUnavailability,
    nextUnavState.data['1'],
  );

  if (!finishedUnavState.loaded || !nextUnavState.loaded) {
    return <Spin loading style={{ marginLeft: '50%' }} />;
  }

  return (
    <div className="UnavailabilitiesTab">
      <Collapse
        defaultActiveKey={[initPanel]}
        accordion
        expandIconPosition="right"
      >
        {currentUnavailability && (
          <Collapse.Panel
            header={
              <span>
                <ClockCircleFilled />
                {' En cours...'}
              </span>
            }
            key="current"
          >
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Début">
                {moment(currentUnavailability.startDate).format(
                  'DD/MM/YYYY HH:mm',
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Fin">
                {moment(currentUnavailability.endDate).format(
                  'DD/MM/YYYY HH:mm',
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {currentUnavailability.type === 'PLANNED'
                  ? 'Maintenance planifiée'
                  : currentUnavailability.type === 'FORCED' ||
                    currentUnavailability.type === 'UNPLANNED'
                  ? 'Arrêt fortuit'
                  : currentUnavailability.type}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <span style={{ color: '#52c41a' }}>
                  {currentUnavailability.status === 'ACTIVE'
                    ? 'En cours'
                    : currentUnavailability.status}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Puissance disponible">
                <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                  {`${currentUnavailability.availablePower_MW || 0} MW`}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Puissance indisponible">
                <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                  {`${currentUnavailability.unavailablePower_MW || 0} MW`}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Raison" span={2}>
                {currentUnavailability.reason}
              </Descriptions.Item>
              {currentUnavailability.remarks && (
                <Descriptions.Item label="Remarques" span={2}>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                    {currentUnavailability.remarks}
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Collapse.Panel>
        )}
        {nextUnavState.data['1'] && nextUnavState.data['1'].length > 0 && (
          <Collapse.Panel
            header={
              <span>
                <ScheduleFilled />
                {`  À venir (${nextUnavState.total || ''})`}
              </span>
            }
            key="next"
          >
            <Table
              loading={!nextUnavState.data[nextUnavState.page]}
              size="small"
              bordered={false}
              scroll={{ x: 800 }}
              pagination={
                nextUnavState.total > PAGE_SIZE
                  ? {
                      current: nextUnavState.page,
                      total: nextUnavState.total,
                      pageSize: PAGE_SIZE,
                      showSizeChanger: false,
                      showQuickJumper: nextUnavState.total > 50,
                    }
                  : false
              }
              expandedRowRender={u => expandedRowRender(u, false)}
              expandRowByClick
              indentSize={0}
              rowKey="id"
              columns={COLUMNS}
              dataSource={nextUnavState.data[nextUnavState.page]}
              onChange={
                pager => setNextUnav({ ...nextUnavState, page: pager.current })
                // eslint-disable-next-line react/jsx-curly-newline
              }
            />
          </Collapse.Panel>
        )}
        <Collapse.Panel
          header={
            <span>
              <CheckSquareFilled />
              {`  Passées (${finishedUnavState.total || ''})`}
            </span>
          }
          key="done"
        >
          <Table
            loading={!finishedUnavState.data[finishedUnavState.page]}
            size="small"
            bordered={false}
            scroll={{ x: 800 }}
            pagination={
              finishedUnavState.total > PAGE_SIZE
                ? {
                    current: finishedUnavState.page,
                    total: finishedUnavState.total,
                    pageSize: PAGE_SIZE,
                    showSizeChanger: false,
                    showQuickJumper: finishedUnavState.total > 50,
                  }
                : false
            }
            expandedRowRender={u => expandedRowRender(u, true)}
            expandRowByClick
            indentSize={0}
            rowKey="id"
            columns={COLUMNS}
            dataSource={finishedUnavState.data[finishedUnavState.page]}
            onChange={
              pager =>
                setFinishedUnav({ ...finishedUnavState, page: pager.current })
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
        </Collapse.Panel>
      </Collapse>
    </div>
  );
}

UnavailabilitiesTab.propTypes = {
  reactor: ReactorType.isRequired,
  currentDate: PropTypes.string.isRequired,
};

export default UnavailabilitiesTab;
